import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, ChevronDown, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { RoomGuest } from "./MultiroomGuestPicker";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface GuestPickerProps {
  guests: { adults: number; children: number; rooms: number; childAges?: number[]; roomsConfig?: RoomGuest[] };
  onChange: (guests: { adults: number; children: number; rooms: number; childAges?: number[]; roomsConfig?: RoomGuest[] }) => void;
  onClose: () => void;
}

export const GuestPicker = ({ guests, onChange, onClose }: GuestPickerProps) => {
  const [childAges, setChildAges] = useState<number[]>(
    guests.childAges || (guests.children > 0 ? Array(guests.children).fill(5) : [])
  );
  const [isRoomDistributionExpanded, setIsRoomDistributionExpanded] = useState(false);
  // REMOVED: Local roomsConfig state - using guests.roomsConfig as single source of truth
  const [activeRoomTab, setActiveRoomTab] = useState(0);
  const [isChildAgesExpanded, setIsChildAgesExpanded] = useState(true);
  const [isManuallyCustomized, setIsManuallyCustomized] = useState(false);
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  
  // Computed value: use guests.roomsConfig directly from props
  const roomsConfig = guests.roomsConfig || [];

  // Sync childAges with prop changes
  useEffect(() => {
    if (guests.childAges && JSON.stringify(guests.childAges) !== JSON.stringify(childAges)) {
      setChildAges(guests.childAges);
    }
  }, [guests.childAges]);


  // Smooth scroll to active tab
  const scrollToActiveTab = (tabIndex: number) => {
    if (tabsContainerRef.current) {
      const container = tabsContainerRef.current;
      const activeTab = container.children[tabIndex] as HTMLElement;
      
      if (activeTab) {
        const containerWidth = container.offsetWidth;
        const tabLeft = activeTab.offsetLeft;
        const tabWidth = activeTab.offsetWidth;
        const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);
        
        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  // Auto-scroll when active tab changes
  useEffect(() => {
    scrollToActiveTab(activeRoomTab);
  }, [activeRoomTab]);

  // Initialize rooms config if switching to multiroom mode - ensures minimum 1 adult per room
  // and respects ETG per-room limits: max 6 adults, max 4 children per room
  const initializeRoomsConfig = (): RoomGuest[] => {
    // If roomsConfig exists but count doesn't match, we need to rebuild
    if (guests.roomsConfig && guests.roomsConfig.length === guests.rooms) {
      return guests.roomsConfig;
    }
    
    const MAX_ADULTS_PER_ROOM = 6;
    const MAX_CHILDREN_PER_ROOM = 4;
    
    // Smart auto-distribute guests across rooms - ensuring minimum 1 adult per room
    // and respecting per-room limits
    
    // Ensure at least 1 adult per room
    const adultsPerRoom = Array(guests.rooms).fill(1); // Start with 1 adult each
    let remainingAdults = guests.adults - guests.rooms; // Extra adults to distribute
    
    // Distribute remaining adults in round-robin while respecting max per room
    let roomIndex = 0;
    while (remainingAdults > 0) {
      if (adultsPerRoom[roomIndex] < MAX_ADULTS_PER_ROOM) {
        adultsPerRoom[roomIndex]++;
        remainingAdults--;
      }
      roomIndex = (roomIndex + 1) % guests.rooms;
      
      // Safety: if all rooms are at max, stop
      if (adultsPerRoom.every(a => a >= MAX_ADULTS_PER_ROOM)) break;
    }
    
    // Round-robin distribution for children while respecting max per room
    const childrenDistribution: number[] = Array(guests.rooms).fill(0);
    let childRoomIndex = 0;
    for (let i = 0; i < guests.children; i++) {
      // Find next room that can accept a child
      let attempts = 0;
      while (childrenDistribution[childRoomIndex] >= MAX_CHILDREN_PER_ROOM && attempts < guests.rooms) {
        childRoomIndex = (childRoomIndex + 1) % guests.rooms;
        attempts++;
      }
      if (childrenDistribution[childRoomIndex] < MAX_CHILDREN_PER_ROOM) {
        childrenDistribution[childRoomIndex]++;
      }
      childRoomIndex = (childRoomIndex + 1) % guests.rooms;
    }
    
    let remainingChildren = [...childAges];
    const config: RoomGuest[] = [];
    
    for (let i = 0; i < guests.rooms; i++) {
      const roomChildrenCount = childrenDistribution[i];
      const roomChildren = remainingChildren.splice(0, roomChildrenCount);
      
      config.push({
        adults: adultsPerRoom[i],
        children: roomChildren
      });
    }
    
    return config;
  };

  // Auto-initialize room distribution when rooms > 1
  useEffect(() => {
    if (guests.rooms > 1 && !guests.roomsConfig) {
      const config = initializeRoomsConfig();
      onChange({ ...guests, roomsConfig: config });
      
      // Auto-collapse child ages section when switching to multiroom
      if (guests.children > 0) {
        setIsChildAgesExpanded(false);
      }
      
      // Scroll to last tab when adding rooms
      setTimeout(() => scrollToActiveTab(guests.rooms - 1), 100);
    } else if (guests.rooms === 1 && guests.roomsConfig) {
      const { roomsConfig: _, ...guestsWithoutConfig } = guests;
      onChange(guestsWithoutConfig);
    }
  }, [guests.rooms]);

  // REMOVED: Auto-sync useEffect that was interfering with manual room distribution
  // Manual distribution is now controlled exclusively through updateRoomGuest()

  const updateRoomGuest = (roomIndex: number, field: 'adults' | 'children', value: number) => {
    // Validate room index
    if (roomIndex < 0 || roomIndex >= roomsConfig.length) {
      console.error(`❌ Invalid room index: ${roomIndex}, roomsConfig length: ${roomsConfig.length}`);
      toast.error('Invalid room selection');
      return;
    }
    
    // Enforce minimum 1 adult per room
    if (field === 'adults' && value < 1) {
      toast.error("Each room must have at least 1 adult");
      return;
    }
    
    // Mark as manually customized when user edits per-room child distribution
    if (field === 'children') {
      setIsManuallyCustomized(true);
    }
    
    console.log(`🔧 updateRoomGuest called:`, { roomIndex, field, value, activeRoomTab, currentConfigLength: roomsConfig.length });
    console.log(`📊 Current config from props:`, JSON.parse(JSON.stringify(roomsConfig)));
    
    // Calculate allocations from current config
    const otherRoomsAdults = roomsConfig.reduce((sum, room, idx) => 
      idx === roomIndex ? sum : sum + room.adults, 0
    );
    const otherRoomsChildren = roomsConfig.reduce((sum, room, idx) => 
      idx === roomIndex ? sum : sum + room.children.length, 0
    );
    
    // Maximum this room can have
    const maxAdults = guests.adults - otherRoomsAdults;
    const maxChildren = guests.children - otherRoomsChildren;
    
    // Validate before updating
    if (field === 'adults' && value > maxAdults) {
      toast.error(`Cannot allocate more than ${maxAdults} adults to this room`);
      return;
    }
    
    if (field === 'children' && value > maxChildren) {
      toast.error(`Cannot allocate more than ${maxChildren} children to this room`);
      return;
    }
    
    // Create new config with the update - directly from current config
    const newConfig = roomsConfig.map((room, idx) => {
      if (idx !== roomIndex) return room;
      
      if (field === 'adults') {
        return { ...room, adults: value };
      } else {
        // Adjust children array
        const currentChildren = room.children.length;
        if (value > currentChildren) {
          return {
            ...room,
            children: [...room.children, ...Array(value - currentChildren).fill(5)]
          };
        } else {
          return {
            ...room,
            children: room.children.slice(0, value)
          };
        }
      }
    });
    
    console.log(`✅ New config:`, JSON.parse(JSON.stringify(newConfig)));
    
    // Update parent immediately with new config - NO LOCAL STATE UPDATE
    const allChildAges = newConfig.flatMap(room => room.children);
    onChange({
      adults: guests.adults,
      children: guests.children,
      rooms: guests.rooms,
      childAges: allChildAges,
      roomsConfig: newConfig
    });
  };

  const updateRoomChildAge = (roomIndex: number, childIndex: number, age: number) => {
    const newConfig = [...roomsConfig];
    newConfig[roomIndex].children[childIndex] = age;
    
    const allChildAges = newConfig.flatMap(room => room.children);
    onChange({ ...guests, childAges: allChildAges, roomsConfig: newConfig });
  };

  const updateGuest = (field: 'adults' | 'children' | 'rooms', increment: boolean) => {
    const value = guests[field] as number;
    const newValue = increment ? value + 1 : value - 1;
    
    // Validation with user feedback
    if (field === "adults" && newValue < 1) return;
    if (field === "children" && newValue < 0) return;
    if (field === "rooms" && newValue < 1) return;
    
    // ETG Limits: 6 adults and 4 children PER ROOM
    // For single room: enforce limits directly
    // For multi-room: each room can have up to 6 adults + 4 children
    const isMultiRoom = guests.rooms > 1;
    
    if (field === "rooms" && newValue > 5) {
      toast.warning("Maximum 5 rooms allowed per booking");
      return;
    }
    
    // Ensure enough adults for rooms (minimum 1 adult per room)
    if (field === "rooms" && increment && guests.adults < newValue) {
      toast.warning(`Need at least ${newValue} adults to book ${newValue} rooms (1 adult per room minimum)`);
      return;
    }
    
    if (!isMultiRoom) {
      // Single room: enforce 6 adults, 4 children, 10 total per room
      if (field === "adults" && newValue > 6) {
        toast.warning("Maximum 6 adults per room");
        return;
      }
      if (field === "children" && newValue > 4) {
        toast.warning("Maximum 4 children per room");
        return;
      }
      const currentAdults = field === "adults" ? newValue : guests.adults;
      const currentChildren = field === "children" ? newValue : guests.children;
      if (currentAdults + currentChildren > 10) {
        toast.warning("Maximum 10 total guests per room");
        return;
      }
    } else {
      // Multi-room: allow up to 6 adults * rooms and 4 children * rooms
      const maxAdults = 6 * guests.rooms;
      const maxChildren = 4 * guests.rooms;
      
      if (field === "adults" && newValue > maxAdults) {
        toast.warning(`Maximum ${maxAdults} adults for ${guests.rooms} rooms (6 per room)`);
        return;
      }
      if (field === "children" && newValue > maxChildren) {
        toast.warning(`Maximum ${maxChildren} children for ${guests.rooms} rooms (4 per room)`);
        return;
      }
    }

    // Handle child ages when children count changes
    if (field === "children") {
      if (increment) {
        const newAges = [...childAges, 5];
        setChildAges(newAges);
        
        // If multi-room: auto-distribute evenly if not manually customized, otherwise add to first room with space
        if (guests.rooms > 1 && roomsConfig.length > 0) {
          const newConfig = [...roomsConfig];
          
          if (!isManuallyCustomized) {
            // Find room with fewest children to maintain even distribution
            let minChildren = Infinity;
            let targetRoomIndex = 0;
            
            for (let i = 0; i < newConfig.length; i++) {
              if (newConfig[i].children.length < minChildren) {
                minChildren = newConfig[i].children.length;
                targetRoomIndex = i;
              }
            }
            
            newConfig[targetRoomIndex].children.push(5);
          } else {
            // User has manually customized - just add to first room with space
            let addedToRoom = false;
            for (let i = 0; i < newConfig.length; i++) {
              const roomTotal = newConfig[i].adults + newConfig[i].children.length;
              if (roomTotal < 10 && newConfig[i].children.length < 4) { // Max 10 guests per room, max 4 children
                newConfig[i].children.push(5);
                addedToRoom = true;
                break;
              }
            }
            if (!addedToRoom) {
              newConfig[0].children.push(5);
            }
          }
          
          onChange({ ...guests, children: newValue, childAges: newAges, roomsConfig: newConfig });
        } else {
          onChange({ ...guests, children: newValue, childAges: newAges });
        }
      } else {
        const newAges = childAges.slice(0, newValue);
        setChildAges(newAges);
        
        // If multi-room: remove child from last room that has children (respects manual customization)
        if (guests.rooms > 1 && roomsConfig.length > 0) {
          const newConfig = [...roomsConfig];
          
          if (!isManuallyCustomized) {
            // Find room with most children to maintain even distribution
            let maxChildren = -1;
            let targetRoomIndex = 0;
            
            for (let i = 0; i < newConfig.length; i++) {
              if (newConfig[i].children.length > maxChildren) {
                maxChildren = newConfig[i].children.length;
                targetRoomIndex = i;
              }
            }
            
            if (newConfig[targetRoomIndex].children.length > 0) {
              newConfig[targetRoomIndex].children.pop();
            }
          } else {
            // User has manually customized - remove from last room with children
            for (let i = newConfig.length - 1; i >= 0; i--) {
              if (newConfig[i].children.length > 0) {
                newConfig[i].children.pop();
                break;
              }
            }
          }
          
          onChange({ ...guests, children: newValue, childAges: newAges, roomsConfig: newConfig });
        } else {
          onChange({ ...guests, children: newValue, childAges: newAges });
        }
      }
      return;
    } else if (field === "adults") {
      // If multi-room: add/remove adult from appropriate room
      if (guests.rooms > 1 && roomsConfig.length > 0) {
        const newConfig = [...roomsConfig];
        if (increment) {
          // Add adult to first room with space
          let addedToRoom = false;
          for (let i = 0; i < newConfig.length; i++) {
            const roomTotal = newConfig[i].adults + newConfig[i].children.length;
            if (roomTotal < 10 && newConfig[i].adults < 6) { // Max 10 guests per room, max 6 adults
              newConfig[i].adults++;
              addedToRoom = true;
              break;
            }
          }
          if (!addedToRoom) {
            newConfig[0].adults++;
          }
        } else {
          // Remove adult from last room that has MORE than 1 adult (keep minimum 1)
          for (let i = newConfig.length - 1; i >= 0; i--) {
            if (newConfig[i].adults > 1) {
              newConfig[i].adults--;
              break;
            }
          }
        }
        onChange({ ...guests, adults: newValue, childAges, roomsConfig: newConfig });
      } else {
        onChange({ ...guests, adults: newValue, childAges });
      }
    } else {
      onChange({ ...guests, [field]: newValue, childAges });
    }
  };

  const updateChildAge = (index: number, age: number) => {
    const newAges = [...childAges];
    newAges[index] = age;
    setChildAges(newAges);
    onChange({ 
      adults: guests.adults, 
      children: guests.children, 
      rooms: guests.rooms, 
      childAges: newAges 
    });
  };

  const handleButtonClick = (e: React.MouseEvent, field: 'adults' | 'children' | 'rooms', increment: boolean) => {
    e.preventDefault();
    e.stopPropagation();
    updateGuest(field, increment);
  };


  // Reusable stepper component
  const Stepper = ({ 
    value, 
    onDecrement, 
    onIncrement, 
    disableMinus, 
    disablePlus 
  }: { 
    value: number; 
    onDecrement: (e: React.MouseEvent) => void; 
    onIncrement: (e: React.MouseEvent) => void; 
    disableMinus: boolean; 
    disablePlus: boolean; 
  }) => (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onDecrement}
        disabled={disableMinus}
        className="w-9 h-9 rounded-full border border-ivory/10 flex items-center justify-center transition-all hover:border-gold/40 hover:bg-gold/[0.06] active:scale-95 disabled:opacity-20 disabled:pointer-events-none"
      >
        <Minus className="w-3 h-3 text-ivory/80" />
      </button>
      <span className="w-10 text-center text-[15px] font-semibold text-ivory tabular-nums">
        {value}
      </span>
      <button
        type="button"
        onClick={onIncrement}
        disabled={disablePlus}
        className="w-9 h-9 rounded-full border border-ivory/10 flex items-center justify-center transition-all hover:border-gold/40 hover:bg-gold/[0.06] active:scale-95 disabled:opacity-20 disabled:pointer-events-none"
      >
        <Plus className="w-3 h-3 text-ivory/80" />
      </button>
    </div>
  );

  const Row = ({ 
    label, 
    sub, 
    children: rowChildren,
    noBorder
  }: { 
    label: string; 
    sub: string; 
    children: React.ReactNode;
    noBorder?: boolean;
  }) => (
    <div className={`flex items-center justify-between py-4 ${!noBorder ? 'border-b border-ivory/[0.06]' : ''}`}>
      <div>
        <p className="text-[14px] font-medium text-ivory/90 tracking-wide">{label}</p>
        <p className="text-[11px] text-ivory/35 mt-0.5 tracking-wide">{sub}</p>
      </div>
      {rowChildren}
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      {/* Adults */}
      <Row label="Adults" sub="Ages 18+">
        <Stepper
          value={guests.adults}
          onDecrement={(e) => handleButtonClick(e, 'adults', false)}
          onIncrement={(e) => handleButtonClick(e, 'adults', true)}
          disableMinus={guests.adults <= 1}
          disablePlus={guests.rooms > 1 ? guests.adults >= 6 * guests.rooms : guests.adults >= 6}
        />
      </Row>

      {/* Children */}
      <Row label="Children" sub="Ages 0–17">
        <Stepper
          value={guests.children}
          onDecrement={(e) => handleButtonClick(e, 'children', false)}
          onIncrement={(e) => handleButtonClick(e, 'children', true)}
          disableMinus={guests.children <= 0}
          disablePlus={guests.rooms > 1 ? guests.children >= 4 * guests.rooms : guests.children >= 4}
        />
      </Row>

      {/* Child Ages */}
      <AnimatePresence>
        {guests.children > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Collapsible open={isChildAgesExpanded} onOpenChange={setIsChildAgesExpanded}>
              <div className="py-4 border-b border-ivory/[0.06]">
                <CollapsibleTrigger asChild>
                  <button className="w-full flex items-center justify-between group">
                    <span className="text-[12px] text-ivory/40 uppercase tracking-[0.12em] font-medium">
                      Child ages
                    </span>
                    <ChevronDown className={`w-3.5 h-3.5 text-gold/50 transition-transform duration-200 ${isChildAgesExpanded ? 'rotate-180' : ''}`} />
                  </button>
                </CollapsibleTrigger>

                {/* Collapsed inline preview */}
                {!isChildAgesExpanded && (
                  <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                    {childAges.slice(0, guests.children).map((age, idx) => (
                      <span key={idx} className="text-[12px] text-ivory/60 bg-ivory/[0.04] border border-ivory/[0.06] rounded-md px-2 py-0.5">
                        {age}y
                      </span>
                    ))}
                  </div>
                )}

                <CollapsibleContent>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.15 }}
                    className="mt-3 grid grid-cols-2 gap-2"
                  >
                    {childAges.slice(0, guests.children).map((age, index) => (
                      <div key={index}>
                        <span className="text-[10px] text-ivory/30 uppercase tracking-wider mb-1 block">Child {index + 1}</span>
                        <select
                          value={age}
                          onChange={(e) => updateChildAge(index, parseInt(e.target.value))}
                          className="w-full h-9 px-3 bg-[hsl(0_0%_8%)] border border-ivory/[0.08] rounded-lg text-[13px] text-ivory/90 font-medium appearance-none cursor-pointer hover:border-gold/30 focus:border-gold/40 focus:ring-1 focus:ring-gold/10 transition-all"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%23eac177' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.5rem center',
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: '1em 1em',
                            paddingRight: '2rem',
                          }}
                        >
                          {Array.from({ length: 18 }, (_, i) => (
                            <option key={i} value={i} className="bg-charcoal text-ivory">
                              {i} {i === 1 ? 'year' : 'years'}
                            </option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </motion.div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rooms */}
      <Row label="Rooms" sub="Up to 5" noBorder={guests.rooms <= 1}>
        <Stepper
          value={guests.rooms}
          onDecrement={(e) => handleButtonClick(e, 'rooms', false)}
          onIncrement={(e) => handleButtonClick(e, 'rooms', true)}
          disableMinus={guests.rooms <= 1}
          disablePlus={guests.rooms >= 5}
        />
      </Row>

      {/* Room Distribution */}
      <AnimatePresence>
        {guests.rooms > 1 && roomsConfig.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
          >
            <Collapsible open={isRoomDistributionExpanded} onOpenChange={setIsRoomDistributionExpanded}>
              {(() => {
                const assignedAdults = roomsConfig.reduce((sum, room) => sum + room.adults, 0);
                const assignedChildren = roomsConfig.reduce((sum, room) => sum + room.children.length, 0);
                const unassignedAdults = guests.adults - assignedAdults;
                const unassignedChildren = guests.children - assignedChildren;
                const hasUnassigned = unassignedAdults > 0 || unassignedChildren > 0;

                return (
                  <div className="pt-4 border-t border-ivory/[0.06]">
                    {/* Unassigned warning */}
                    {hasUnassigned && !isRoomDistributionExpanded && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="mb-3 px-3 py-2 rounded-lg bg-amber-500/[0.06] border border-amber-500/20"
                      >
                        <p className="text-[12px] text-amber-400/90">
                          {unassignedAdults > 0 && `${unassignedAdults} adult${unassignedAdults !== 1 ? 's' : ''}`}
                          {unassignedAdults > 0 && unassignedChildren > 0 && ', '}
                          {unassignedChildren > 0 && `${unassignedChildren} child${unassignedChildren !== 1 ? 'ren' : ''}`}
                          {' unassigned'}
                        </p>
                      </motion.div>
                    )}

                    <CollapsibleTrigger asChild>
                      <button className="w-full flex items-center justify-between group pb-3">
                        <div>
                          <span className="text-[12px] text-ivory/40 uppercase tracking-[0.12em] font-medium">
                            Room distribution
                          </span>
                          {!isRoomDistributionExpanded && (
                            <p className="text-[12px] text-ivory/50 mt-1">
                              {roomsConfig.map((r, i) => (
                                <span key={i}>
                                  {i > 0 && <span className="text-gold/30 mx-1.5">·</span>}
                                  {r.adults}A{r.children.length > 0 && `+${r.children.length}C`}
                                </span>
                              ))}
                            </p>
                          )}
                        </div>
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${hasUnassigned ? 'text-amber-400/60' : 'text-gold/40'} ${isRoomDistributionExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent>
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.15 }}
                      >
                        {/* Room tabs */}
                        <div className="relative mb-4">
                          <div
                            ref={tabsContainerRef}
                            className="flex gap-0.5 overflow-x-auto scrollbar-none"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                          >
                            {roomsConfig.map((room, roomIndex) => (
                              <button
                                key={roomIndex}
                                onClick={() => {
                                  setActiveRoomTab(roomIndex);
                                  scrollToActiveTab(roomIndex);
                                }}
                                className={`flex-shrink-0 px-4 py-2 text-[12px] font-medium rounded-lg transition-all ${
                                  activeRoomTab === roomIndex
                                    ? 'bg-gold/[0.1] text-gold border border-gold/20'
                                    : 'text-ivory/40 hover:text-ivory/60 border border-transparent'
                                }`}
                              >
                                <span>Room {roomIndex + 1}</span>
                                <span className="block text-[10px] font-normal mt-0.5 opacity-60">
                                  {room.adults}A{room.children.length > 0 ? `·${room.children.length}C` : ''}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Active room config */}
                        <AnimatePresence mode="wait">
                          {(() => {
                            const otherRoomsAdults = roomsConfig.reduce((sum, room, idx) =>
                              idx === activeRoomTab ? sum : sum + room.adults, 0
                            );
                            const otherRoomsChildren = roomsConfig.reduce((sum, room, idx) =>
                              idx === activeRoomTab ? sum : sum + room.children.length, 0
                            );
                            const maxAdultsForThisRoom = guests.adults - otherRoomsAdults;
                            const maxChildrenForThisRoom = guests.children - otherRoomsChildren;

                            return (
                              <motion.div
                                key={`room-${activeRoomTab}-adults-${roomsConfig[activeRoomTab]?.adults || 0}-children-${roomsConfig[activeRoomTab]?.children.length || 0}`}
                                initial={{ opacity: 0, x: 6 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -6 }}
                                transition={{ duration: 0.15 }}
                                className="p-4 rounded-xl bg-ivory/[0.02] border border-ivory/[0.06]"
                              >
                                {/* Status line */}
                                {(() => {
                                  const availAdults = maxAdultsForThisRoom - roomsConfig[activeRoomTab].adults;
                                  const availChildren = maxChildrenForThisRoom - roomsConfig[activeRoomTab].children.length;
                                  const allAssigned = availAdults === 0 && availChildren === 0;

                                  return (
                                    <p className={`text-[11px] mb-3 ${allAssigned ? 'text-emerald-400/70' : 'text-ivory/30'}`}>
                                      {allAssigned
                                        ? '✓ All guests assigned'
                                        : `${availAdults} adult${availAdults !== 1 ? 's' : ''}${availChildren > 0 ? `, ${availChildren} child${availChildren !== 1 ? 'ren' : ''}` : ''} available`
                                      }
                                    </p>
                                  );
                                })()}

                                {/* Per-room adults */}
                                <div className="flex items-center justify-between py-2.5 border-b border-ivory/[0.05]">
                                  <span className="text-[13px] text-ivory/70">Adults</span>
                                  <Stepper
                                    value={roomsConfig[activeRoomTab].adults}
                                    onDecrement={() => {
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'adults', Math.max(1, currentRoom.adults - 1));
                                    }}
                                    onIncrement={() => {
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'adults', currentRoom.adults + 1);
                                    }}
                                    disableMinus={roomsConfig[activeRoomTab].adults <= 1}
                                    disablePlus={roomsConfig[activeRoomTab].adults >= maxAdultsForThisRoom || roomsConfig[activeRoomTab].adults >= 6}
                                  />
                                </div>

                                {/* Per-room children */}
                                <div className="flex items-center justify-between py-2.5">
                                  <span className="text-[13px] text-ivory/70">Children</span>
                                  <Stepper
                                    value={roomsConfig[activeRoomTab].children.length}
                                    onDecrement={() => {
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'children', Math.max(0, currentRoom.children.length - 1));
                                    }}
                                    onIncrement={() => {
                                      const currentRoom = roomsConfig[activeRoomTab];
                                      if (!currentRoom) return;
                                      updateRoomGuest(activeRoomTab, 'children', currentRoom.children.length + 1);
                                    }}
                                    disableMinus={roomsConfig[activeRoomTab].children.length <= 0}
                                    disablePlus={roomsConfig[activeRoomTab].children.length >= maxChildrenForThisRoom || roomsConfig[activeRoomTab].children.length >= 4}
                                  />
                                </div>
                              </motion.div>
                            );
                          })()}
                        </AnimatePresence>
                      </motion.div>
                    </CollapsibleContent>
                  </div>
                );
              })()}
            </Collapsible>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Quiet save indicator */}
      <div className="flex items-center justify-center gap-1.5 pt-4">
        <Check className="w-3 h-3 text-emerald-500/50" />
        <span className="text-[10px] text-ivory/25 tracking-wider uppercase">Auto-saved</span>
      </div>
    </motion.div>
  );
};
